// Webster Notetaker bot-status presentation. Color is never the sole carrier of meaning, so every
// badge pairs a dot with a label. Palette + labels come from ./websterStatus so the badge and the
// calendar/list dot always agree.
import { WEBSTER_STATUS, WEBSTER_STATUS_SILENT_ON_LIST, humanizeSubCode, type WebsterTone } from './websterStatus'

function DotMark({ tone }: { tone: WebsterTone }) {
  return (
    <span class="relative inline-flex h-1.5 w-1.5 shrink-0">
      {tone.pulse && (
        <span class={`absolute inline-flex h-full w-full rounded-full opacity-60 motion-safe:animate-ping ${tone.dot}`} aria-hidden="true" />
      )}
      <span class={`relative inline-flex h-1.5 w-1.5 rounded-full ${tone.dot}`} />
    </span>
  )
}

interface BadgeProps {
  status?: string | null
  /** recall.ai failure reason; surfaced in the tooltip when the bot failed. */
  subCode?: string | null
  className?: string
}

/** Labeled status pill for the meeting editor. Renders nothing for an unknown status. */
export function WebsterBotStatusBadge({ status, subCode, className = '' }: BadgeProps) {
  const cfg = status ? WEBSTER_STATUS[status] : undefined
  if (!cfg) return null
  const reason = status === 'Failed' && subCode ? humanizeSubCode(subCode) : ''
  return (
    <span
      class={`inline-flex items-center gap-1.5 rounded-full pl-1.5 pr-2 py-0.5 text-[11.5px] font-semibold ${cfg.tone.pill} ${className}`}
      title={reason ? `Webster failed: ${reason}` : undefined}
    >
      <DotMark tone={cfg.tone} />
      {cfg.label}
      {reason && <span class="sr-only">: {reason}</span>}
    </span>
  )
}

interface DotProps {
  status?: string | null
  className?: string
}

/** Bare status dot for dense surfaces (meeting list, calendar). Silent when there is no bot. */
export function WebsterStatusDot({ status, className = '' }: DotProps) {
  const cfg = status ? WEBSTER_STATUS[status] : undefined
  if (!cfg || (status && WEBSTER_STATUS_SILENT_ON_LIST.has(status))) return null
  return (
    <span class={`inline-flex ${className}`} title={`Webster: ${cfg.label}`} aria-label={`Webster: ${cfg.label}`}>
      <DotMark tone={cfg.tone} />
    </span>
  )
}
